/*#
# Skills: one copy, served by the board

A skill is instructions every agent follows, so a second installed copy
that drifts is a review run with last week's rules (board #2477, #115). The
board holds the one copy. What a machine installs is a **stub** with no
logic: it runs `virta skill run <name>`, which prints the current text and
writes the skill's files where the text says they are.

- **A skill is a Library entry**, `type:skill` with `skill:<name>`. Its
  body is the skill's text followed by its files, so the text and the
  script it runs are always one version, with the board's history and
  diffs.
- **Writes are restricted.** A version counts only when the host stamped
  it as written by a person signed in on the board, or when its writer
  acts for a repo the owner named on the entry (`custodian:<repo>`, a tag
  that itself counts only when added by a signed-in person). The repo is
  the writer's own word (`Tosi × tosijs-coding-practices`), the same
  identity on every write: no token per repo, nothing to renew. It keeps
  other projects' agents from changing a skill by mistake; it isn't proof
  against one that lies about who it is (the host's stamp still names the
  machine). Two partial guards: each version records the token that wrote
  it, and one written with a different token than that repo's versions
  before it says so on every run and on the card; and the CLI refuses a
  publish whose repo came from `--identity`. A stricter tag is there when
  wanted: `custodian:<token id>` accepts one token only. Anything else is
  kept, shown as ignored, and never served.
- **Each run says what it ran:** the version, and what changed since this
  repo last ran it (`ran:<project>@<n>` on the entry is that record).
- **A project can pin** a version (`pin:<project>@<n>`): a release in
  progress finishes on the version it started with, and each run says it
  is pinned and how far behind.
- **Offline**, the CLI answers from its replica and says so.

```
virta skill publish pre-release-review ./SKILL.md ./review.workflow.js --note "the docs lens joins pre-minor"
virta skill install pre-release-review     # writes the stub into ~/.claude/skills
virta skill run pre-release-review         # what the stub runs: the current text, its files, what changed
virta skill pin pre-release-review         # this repo stays on the current version
virta skill unpin pre-release-review
virta skill list
```

In the text, `{{skill-dir}}` is where this machine holds the skill's files.
*/

/*{ "parent": "Reference", "order": 43 }*/

import type { Task, VirtaEvent } from './model'
import { textHash } from './model'
import type { VirtaStore } from './store'
import { tagValues, TYPE_KEY } from './tags'
import { whoWrote } from './transcript'

export const SKILL_TYPE = 'skill'
/** The tag naming a skill: `skill:pre-release-review`. */
export const SKILL_KEY = 'skill'
/** The tag naming who may change a skill: `custodian:<repo>` (its agents), or the stricter `custodian:<token id>`. */
export const CUSTODIAN_KEY = 'custodian'

/** A project's pin: `pin:<project>@<version>`. */
export const PIN_KEY = 'pin'
/** The version a project last ran: `ran:<project>@<version>`. */
export const RAN_KEY = 'ran'
/** In a skill's text: where the machine running it holds its files. */
export const SKILL_DIR = '{{skill-dir}}'
/** A skill and its files, together: an event is one host document. */
export const SKILL_MAX_BYTES = 600_000

const NAME = /^[a-z0-9][a-z0-9-]*$/
const FILE_NAME = /^[A-Za-z0-9][A-Za-z0-9._-]*$/
const FILE_MARK = /^<!-- virta:file (\S+) -->$/

/** Why a file name can't be part of a skill, or ''. A plain name: it becomes a path on every machine. */
export function fileNameRefusal(name: string): string {
  if (!FILE_NAME.test(name) || name.includes('..'))
    return `${name}: a skill's file has a plain name (letters, digits, . _ -), no folders`
  if (name.toLowerCase() === 'skill.md')
    return `${name}: the skill's text is its own argument, not one of its files`
  return ''
}

/** A skill's text and files as one body: the text, then each file in a fence longer than any it holds. */
export function bundleSkill(
  text: string,
  files: Readonly<Record<string, string>> = {}
): string {
  const parts = [text.trimEnd() + '\n']
  for (const name of Object.keys(files).sort()) {
    const content = files[name]!
    let longest = 3
    for (const m of content.matchAll(/^\s*(`{3,})/gm))
      longest = Math.max(longest, m[1]!.length)
    const fence = '`'.repeat(longest + 1)
    const lang = /\.([A-Za-z0-9]+)$/.exec(name)?.[1] ?? ''
    parts.push(
      `\n<!-- virta:file ${name} -->\n${fence}${lang}\n${content}${
        content.endsWith('\n') ? '' : '\n'
      }${fence}\n`
    )
  }
  return parts.join('')
}

/** A body back into the skill's text and files. A file with a name that can't be a path is dropped. */
export function unbundleSkill(body: string): {
  text: string
  files: Record<string, string>
} {
  const lines = body.split('\n')
  const files: [string, string][] = []
  let textEnd = lines.length
  for (let i = 0; i < lines.length; i++) {
    const mark = FILE_MARK.exec(lines[i]!)
    const fence = /^(`{3,})[A-Za-z0-9]*$/.exec(lines[i + 1] ?? '')
    if (!mark || !fence) continue
    const end = lines.indexOf(fence[1]!, i + 2)
    if (end < 0) continue
    textEnd = Math.min(textEnd, i)
    if (!fileNameRefusal(mark[1]!))
      files.push([mark[1]!, lines.slice(i + 2, end).join('\n') + '\n'])
    i = end
  }
  return {
    text: lines.slice(0, textEnd).join('\n').trimEnd() + '\n',
    files: Object.fromEntries(files),
  }
}

/** The `description:` in a skill's front matter: what tells an agent when to use it. */
export function skillDescription(text: string): string {
  const front = /^---\n([\s\S]*?)\n---/.exec(text)?.[1] ?? ''
  return /^description:\s*(.+)$/m.exec(front)?.[1]?.trim() ?? ''
}

/** What the skills read: tasks and their events (a store is one). */
export interface SkillBoard {
  tasks: Iterable<Task>
  events(taskId: string): VirtaEvent[]
  /** who wrote an event, corrections applied (a store has it; board #3316) */
  whoWrote?(event: VirtaEvent): string
}

const whoBy =
  (board: SkillBoard) =>
  (e: VirtaEvent): string =>
    board.whoWrote ? board.whoWrote(e) : whoWrote(e)

/** Whether the host says a person signed in on the board wrote it (a local log has no stamps: it counts). */
const bySession = (e: VirtaEvent) => !e.attribution || !e.attribution.token

/** The repo a writer says it acts for: `Tosi × tosijs-3d` → `tosijs-3d`; '' when it names none. */
export function actingFor(by: string): string {
  const cut = by.indexOf(' × ')
  return cut < 0 ? '' : by.slice(cut + 3).trim()
}

/**
 * Who may change a skill: each `custodian:<repo or token id>` the entry
 * carries whose latest adding was by a signed-in person. An agent can't
 * name its own repo or token, or another.
 */
export function custodiansOf(
  entry: Task,
  events: readonly VirtaEvent[]
): string[] {
  return tagValues(entry.tags, CUSTODIAN_KEY).filter((id) => {
    const added = events
      .filter(
        (e) => e.kind === 'tagged' && e.payload.tag === `${CUSTODIAN_KEY}:${id}`
      )
      .pop()
    return !!added && bySession(added)
  })
}

export interface SkillVersion {
  version: number
  /** the event that wrote it */
  event: string
  at: string
  by: string
  body: string
  /** what the writer said changed, or '' */
  note: string
  /** the token that wrote it, as the host stamped it; '' for a person signed in */
  token: string
  /** '' or what to look at: written with a different token than this repo's versions before it */
  caution: string
}

/** An edit that isn't served, and why. */
export interface IgnoredEdit {
  event: string
  at: string
  by: string
  why: string
}

/**
 * A skill's versions: the bodies written by someone who may, oldest
 * first, numbered from 1. An edit by anyone else, or one made from an
 * older body while another landed (a conflict the board flags), is listed
 * as ignored and never served.
 */
export function skillVersions(
  entry: Task,
  events: readonly VirtaEvent[],
  // who wrote each, as the board shows it: `store.whoWrote`, so a relabel
  // shows here as it does in history (board #3316)
  whoOf: (e: VirtaEvent) => string = (e) => whoWrote(e)
): { versions: SkillVersion[]; ignored: IgnoredEdit[] } {
  const custodians = custodiansOf(entry, events)
  const versions: SkillVersion[] = []
  const ignored: IgnoredEdit[] = []
  let current = ''
  for (const e of events) {
    if (e.kind !== 'created' && e.kind !== 'edited') continue
    const body = e.payload.body
    const base = e.kind === 'edited' ? e.payload.base : undefined
    const concurrent =
      base !== undefined && base !== textHash(current) && body !== current
    if (!concurrent) current = body
    const repo = actingFor(e.by)
    const token = e.attribution?.token ?? ''
    const who = whoOf(e)
    if (concurrent)
      ignored.push({
        event: e.id,
        at: e.at,
        by: who,
        why: 'made from an older version while another landed',
      })
    else if (
      !bySession(e) &&
      !custodians.includes(repo) &&
      !custodians.includes(token)
    )
      ignored.push({
        event: e.id,
        at: e.at,
        by: who,
        why: repo
          ? `${repo} isn't a custodian (the owner adds ${CUSTODIAN_KEY}:${repo} on the board to accept its writes)`
          : "its writer names no repo, so it can't be a custodian",
      })
    else if (body !== versions.at(-1)?.body) {
      // the same repo, a token its versions so far didn't use: a renewed
      // login, another machine — or a write from the wrong place
      const before = versions.filter((v) => v.token && actingFor(v.by) === repo)
      const caution =
        token && before.length && !before.some((v) => v.token === token)
          ? `written with a different token (${token.slice(
              0,
              8
            )}) than ${repo}'s versions before it: a renewed login or another machine, if you expected one`
          : ''
      versions.push({
        token,
        caution,
        version: versions.length + 1,
        event: e.id,
        at: e.at,
        by: who,
        body,
        note:
          e.kind === 'edited' ? String(e.payload.note ?? '').trim() : 'first',
      })
    }
  }
  return { versions, ignored }
}

const isSkill = (t: Task) =>
  tagValues(t.tags, TYPE_KEY)[0] === SKILL_TYPE && !t.closedAt

/**
 * The entry that is a skill: open, `type:skill`, `skill:<name>`. If more
 * than one claims the name, one with a served version wins (so a second
 * entry can't take a name over), then the oldest.
 */
export function findSkill(board: SkillBoard, name: string): Task | null {
  const claims = Array.from(board.tasks)
    .filter((t) => isSkill(t) && tagValues(t.tags, SKILL_KEY).includes(name))
    .sort((a, b) => a.num - b.num)
  return (
    claims.find(
      (t) =>
        skillVersions(t, board.events(t.id), whoBy(board)).versions.length > 0
    ) ??
    claims[0] ??
    null
  )
}

/** `pin:<project>@4` → 4, for a project; null when it has none. */
function versionTagged(
  entry: Task,
  key: string,
  project: string
): number | null {
  for (const value of tagValues(entry.tags, key)) {
    const at = value.lastIndexOf('@')
    if (at > 0 && value.slice(0, at) === project) {
      const n = Number(value.slice(at + 1))
      if (Number.isInteger(n) && n > 0) return n
    }
  }
  return null
}

/** How many lines came and went between two bodies (counted, not aligned: a body with a script is long). */
function lineChange(before: string, after: string): string {
  const count = (text: string) => {
    const lines = new Map<string, number>()
    for (const l of text.split('\n')) lines.set(l, (lines.get(l) ?? 0) + 1)
    return lines
  }
  const a = count(before)
  const b = count(after)
  let added = 0
  let removed = 0
  for (const [l, n] of b) added += Math.max(0, n - (a.get(l) ?? 0))
  for (const [l, n] of a) removed += Math.max(0, n - (b.get(l) ?? 0))
  return `+${added} −${removed} lines`
}

export interface SkillRead {
  name: string
  /** the entry's number on the board */
  num: number
  /** the version this is */
  version: number
  /** the newest served version */
  latest: number
  /** whether the project pinned this version */
  pinned: boolean
  /** how many versions newer than this one exist */
  behind: number
  /** '' or what to look at about who wrote this version (a token its repo hadn't used) */
  caution: string
  at: string
  by: string
  description: string
  text: string
  files: Record<string, string>
  /** the version this project ran last, or null */
  lastRan: number | null
  /** one line: what changed since this project last ran it */
  changed: string
  /** each version since, newest last */
  changes: string[]
  /** edits that aren't served, and why */
  ignored: string[]
}

/** What each version after `after` changed: its note, or how many lines came and went. */
function changesSince(
  versions: SkillVersion[],
  after: number,
  through: number
) {
  return versions
    .filter((v) => v.version > after && v.version <= through)
    .map((v) => ({
      version: v,
      what:
        v.note && v.note !== 'first'
          ? v.note
          : lineChange(versions[v.version - 2]?.body ?? '', v.body),
    }))
}

/**
 * A skill as a project runs it: its pinned version, or the newest. Throws
 * with what to do when there is no such skill, or none of its versions is
 * served yet.
 */
export function readSkill(
  board: SkillBoard,
  input: { name: string; project?: string; version?: number }
): SkillRead {
  const entry = findSkill(board, input.name)
  if (!entry) {
    const names = skillSummaries(board).map((s) => s.name)
    throw new Error(
      `no skill named "${input.name}" on the board${
        names.length ? ` (there: ${names.join(', ')})` : ''
      }`
    )
  }
  const { versions, ignored } = skillVersions(
    entry,
    board.events(entry.id),
    whoBy(board)
  )
  if (!versions.length)
    throw new Error(
      `skill "${input.name}" (#${entry.num}) has no served version: ${
        ignored.map((i) => `${i.by}'s edit — ${i.why}`).join('; ') ||
        'it has no text'
      }`
    )
  const latest = versions.length
  const project = input.project ?? ''
  const pin = project ? versionTagged(entry, PIN_KEY, project) : null
  const wanted = input.version ?? pin ?? latest
  const chosen = versions[wanted - 1]
  if (!chosen)
    throw new Error(
      `skill "${input.name}" has no version ${wanted} (it has 1 to ${latest})`
    )
  const lastRan = project ? versionTagged(entry, RAN_KEY, project) : null
  const { text, files } = unbundleSkill(chosen.body)
  const changes =
    lastRan !== null && lastRan < chosen.version
      ? changesSince(versions, lastRan, chosen.version)
      : []
  const changed =
    lastRan === null
      ? project
        ? `first run in ${project}`
        : ''
      : lastRan === chosen.version
      ? `unchanged since ${project} last ran it`
      : lastRan > chosen.version
      ? `${project} last ran v${lastRan}; this is the older v${chosen.version}`
      : `since ${project} last ran it (v${lastRan}): ${changes
          .map((c) => c.what)
          .join('; ')}`
  return {
    name: input.name,
    num: entry.num,
    version: chosen.version,
    latest,
    pinned: pin !== null && input.version === undefined,
    behind: latest - chosen.version,
    caution: chosen.caution,
    at: chosen.at,
    by: chosen.by,
    description: skillDescription(text),
    text,
    files,
    lastRan,
    changed,
    // one line per version: `v5 2026-10-08 Tosi × practices: the docs lens joins pre-minor`
    changes: changes.map(
      (c) =>
        `v${c.version.version} ${c.version.at.slice(0, 10)} ${c.version.by}: ${
          c.what
        }${c.version.caution ? ` (${c.version.caution})` : ''}`
    ),
    ignored: ignored.map((i) => `${i.at.slice(0, 10)} ${i.by}: ${i.why}`),
  }
}

/** The first line of a run: which version, pinned or not, and what changed. */
export function runHeader(r: SkillRead): string {
  const pin = r.pinned
    ? `, pinned${
        r.behind
          ? ` — ${r.behind} behind (v${r.latest} is current)`
          : ' at the current version'
      }`
    : r.behind
    ? ` (v${r.latest} is current)`
    : ''
  return `${r.name} v${r.version}${pin}, ${r.at.slice(0, 10)} by ${r.by}${
    r.changed ? `. ${r.changed[0]!.toUpperCase()}${r.changed.slice(1)}.` : '.'
  }${r.caution ? ` Check: this version was ${r.caution}.` : ''}`
}

export interface SkillSummary {
  name: string
  num: number
  /** the newest served version; 0 while none is served */
  version: number
  description: string
  /** `project@version` for each pin */
  pins: string[]
  /** `project@version` for each project's last run */
  ran: string[]
  /** how many edits aren't served */
  ignored: number
}

/** Every skill on the board, by name. */
export function skillSummaries(board: SkillBoard): SkillSummary[] {
  const names = new Set<string>()
  for (const t of board.tasks)
    if (isSkill(t)) for (const n of tagValues(t.tags, SKILL_KEY)) names.add(n)
  return Array.from(names)
    .sort()
    .map((name) => {
      const entry = findSkill(board, name)!
      const { versions, ignored } = skillVersions(
        entry,
        board.events(entry.id),
        whoBy(board)
      )
      const body = versions.at(-1)?.body ?? ''
      return {
        name,
        num: entry.num,
        version: versions.length,
        description: skillDescription(unbundleSkill(body).text),
        pins: tagValues(entry.tags, PIN_KEY),
        ran: tagValues(entry.tags, RAN_KEY),
        ignored: ignored.length,
      }
    })
}

export interface Published {
  num: number
  /** the newest served version after this write */
  version: number
  /** whether this write is served; false for a draft awaiting the owner */
  served: boolean
  message: string
}

/**
 * Write a skill's text and files: a new version, or the skill's first.
 * `token` says the caller writes with a token, not signed in (the CLI and
 * the MCP server fill it in); who it acts for is the store's identity. An
 * agent whose repo isn't a custodian can file a new skill — a draft nobody
 * runs until the owner names its repo — but can't change an existing one.
 */
export async function publishSkill(
  store: VirtaStore,
  input: {
    name: string
    text: string
    files?: Record<string, string>
    note?: string
    token?: string
    project?: string
  }
): Promise<Published> {
  const { name, text } = input
  if (!NAME.test(name))
    throw new Error(
      `"${name}": a skill's name is lowercase letters, digits and dashes`
    )
  if (!skillDescription(text))
    throw new Error(
      `the skill's text needs front matter with a description (--- / name: ${name} / description: when to use it / ---): it's what tells an agent when to run it`
    )
  const files = input.files ?? {}
  for (const file of Object.keys(files)) {
    const refusal = fileNameRefusal(file)
    if (refusal) throw new Error(refusal)
  }
  const body = bundleSkill(text, files)
  if (body.length > SKILL_MAX_BYTES)
    throw new Error(
      `the skill and its files are ${Math.round(
        body.length / 1000
      )} KB together; the most is ${SKILL_MAX_BYTES / 1000} KB`
    )
  const existing = findSkill(store, name)
  const repo = actingFor(store.identity)
  if (existing && input.token) {
    const custodians = custodiansOf(existing, store.events(existing.id))
    if (!custodians.includes(repo) && !custodians.includes(input.token))
      throw new Error(
        `skill "${name}" (#${
          existing.num
        }) can be changed by the owner on the board, or by an agent acting for a repo the owner names on it. ${
          repo
            ? `To make ${repo} a custodian, the owner adds the tag ${CUSTODIAN_KEY}:${repo} to #${existing.num} on the board (signed in, not from a CLI)`
            : `This write names no repo (it acts as "${store.identity}"): run it from the repo, or pass an identity "<machine> × <repo>"`
        }`
      )
  }
  const entry =
    existing ??
    (await store.create({
      title: `Skill: ${name}`,
      body,
      tags: [
        `${TYPE_KEY}:${SKILL_TYPE}`,
        `${SKILL_KEY}:${name}`,
        ...(input.project ? [`project:${input.project}`] : []),
      ],
    }))
  if (existing) await store.edit(existing.id, body, input.note)
  const { versions } = skillVersions(
    store.get(entry.id)!,
    store.events(entry.id),
    whoBy(store)
  )
  const served = versions.at(-1)?.body === body
  return {
    num: entry.num,
    version: versions.length,
    served,
    message: served
      ? `skill "${name}" is v${versions.length} (#${entry.num}): every machine runs it on its next run`
      : `skill "${name}" is filed as #${
          entry.num
        }, a draft: nothing runs it until the owner adds the tag ${CUSTODIAN_KEY}:${
          repo || '<repo>'
        } to #${
          entry.num
        } on the board (signed in), which accepts that repo's writes. It opens at ${
          store.board
        }#?virta.task=${entry.num} (or type #${
          entry.num
        } in the board's search box, from any view)`,
  }
}

/** Replace a project's `<key>:<project>@<n>` tag on a skill's entry. */
async function setVersionTag(
  store: VirtaStore,
  entry: Task,
  key: string,
  project: string,
  version: number | null
): Promise<void> {
  const wanted = version === null ? '' : `${key}:${project}@${version}`
  const old = entry.tags.filter(
    (t) => t.startsWith(`${key}:${project}@`) && t !== wanted
  )
  if (old.length) await store.untag(entry.id, ...old)
  if (wanted) await store.tag(entry.id, wanted)
}

const requireProject = (project: string | undefined, what: string): string => {
  if (!project)
    throw new Error(
      `${what} is per project: run it in the repo, or name one with project`
    )
  return project
}

/** Pin a project to a version (the newest, unless one is named): it runs that one until unpinned. */
export async function pinSkill(
  store: VirtaStore,
  input: { name: string; project?: string; version?: number }
): Promise<{ message: string }> {
  const project = requireProject(input.project, 'a pin')
  const read = readSkill(store, { name: input.name, version: input.version })
  await setVersionTag(
    store,
    findSkill(store, input.name)!,
    PIN_KEY,
    project,
    read.version
  )
  return {
    message: `${project} runs ${input.name} v${read.version} until unpinned${
      read.behind ? ` (${read.behind} behind: v${read.latest} is current)` : ''
    }`,
  }
}

/** Remove a project's pin: it runs the newest version again. */
export async function unpinSkill(
  store: VirtaStore,
  input: { name: string; project?: string }
): Promise<{ message: string }> {
  const project = requireProject(input.project, 'a pin')
  const read = readSkill(store, { name: input.name })
  await setVersionTag(
    store,
    findSkill(store, input.name)!,
    PIN_KEY,
    project,
    null
  )
  return {
    message: `${project} runs the current ${input.name} (v${read.latest})`,
  }
}

/**
 * Run a skill for a project: what `readSkill` answers, and the board
 * records which version the project ran — written only when it changes,
 * so a run of the same version writes nothing.
 */
export async function runSkill(
  store: VirtaStore,
  input: { name: string; project?: string }
): Promise<SkillRead> {
  const read = readSkill(store, input)
  if (input.project && read.lastRan !== read.version)
    await setVersionTag(
      store,
      findSkill(store, input.name)!,
      RAN_KEY,
      input.project,
      read.version
    )
  return read
}

/** The stub a machine installs: no logic, only where the skill is. */
export function skillStub(name: string, description: string): string {
  return `---
name: ${name}
description: ${description}
---

<!-- virta:stub — installed by \`virta skill install ${name}\`. The skill is on the board, not here (one copy). -->

# ${name}

Run this, from the repo you are working in, and follow what it prints exactly:

\`\`\`bash
virta skill run ${name}
\`\`\`

It prints which version it is and what changed since this repo last ran
it — tell the user that line — then the skill itself. The skill's files
are written where its text says they are.

If the command fails or prints no skill, stop and tell the user what it
said. Don't run the skill from memory or from an old copy.
`
}

/** Whether a SKILL.md is a stub this installed. */
export const isStub = (text: string) => text.includes('<!-- virta:stub')
